import _get from "lodash/get";
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import { CRINKLE_PAPER_ALLOWED_VARIANTS, SPECIAL_PRODUCT_TYPES } from 'swag-common/constants/product.constants';
import { DesignUploadMode } from 'swag-common/interfaces';
import { isBox } from 'swag-common/utils/order/item-is-box.util';
import { showPopup } from 'swag-client-common/redux/popup-manager/popup-manager.actions';
import { EDIT_BOX_NOTECARD_POPUP } from 'swag-client-common/constants';
import { isItemOriginallyInventoryReordered } from 'swag-common/utils/order/is-item-originally-inventory-reordered.util';
import { isItemInventoryReordered } from 'swag-common/utils/order/is-item-inventory-reordered.util';
import { pickedSpecialBoxesProductsSelector, cartItemsSelector, draftItemsSelector, isSpecialBoxProductsAvailableSelector, crinklePaperVariantsSelector, checkoutButtonTouchedSelector } from '../../../../../../redux/cart/cart.selectors';
import { toggleSpecialProductInBox } from '../../../../../../redux/box-builder/box-builder.actions';
import { setBoxNotecardBuilderBoxItemsId } from '../../../../../../redux/box-notecard-editor/box-notecard-editor.actions';
import { BoxNotecardAndCrinklePaperComponent } from './box-notecard-and-crinkle-paper';
const boxItemsIdSelector = (state, props) => props.boxItemsId;
const notecardOptionSelector = createSelector(boxItemsIdSelector, pickedSpecialBoxesProductsSelector, cartItemsSelector, draftItemsSelector, (boxItemsId, pickedSpecialBoxesProducts, cartItems, draftItems) => {
  const allItems = [...cartItems, ...draftItems];
  const isNotecardPresentInBox = allItems.find(item => item.boxItemsId === boxItemsId && item.prod.specialProductType === SPECIAL_PRODUCT_TYPES.BOX_NOTECARD);
  if (isNotecardPresentInBox) {
    return true;
  }
  const boxItem = allItems.find(item => item.boxItemsId === boxItemsId && isBox(item));
  if (boxItem !== null && boxItem !== void 0 && boxItem.isBoxNotecardAbsent) {
    return false;
  }
  return pickedSpecialBoxesProducts[boxItemsId] && pickedSpecialBoxesProducts[boxItemsId].isNotecardPicked !== undefined ? pickedSpecialBoxesProducts[boxItemsId].isNotecardPicked : null;
});
const crinklePaperOptionSelector = createSelector(boxItemsIdSelector, pickedSpecialBoxesProductsSelector, cartItemsSelector, draftItemsSelector, (boxItemsId, pickedSpecialBoxesProducts, cartItems, draftItems) => {
  const boxCrinklePaper = [...cartItems, ...draftItems].find(item => item.boxItemsId === boxItemsId && item.prod.specialProductType === SPECIAL_PRODUCT_TYPES.BOX_CRINKLE_PAPER);
  if (boxCrinklePaper) {
    return _get(boxCrinklePaper, 'variants[0].variantIndex', 0);
  }
  return pickedSpecialBoxesProducts[boxItemsId] && pickedSpecialBoxesProducts[boxItemsId].crinklePaperVariantIndex !== undefined ? pickedSpecialBoxesProducts[boxItemsId].crinklePaperVariantIndex : null;
});
const isSpecialBoxProductsCustomizableSelector = createSelector(isSpecialBoxProductsAvailableSelector, boxItemsIdSelector, cartItemsSelector, draftItemsSelector, (isSpecialBoxProductsAvailable, boxItemsId, cartItems, draftItems) => {
  if (!isSpecialBoxProductsAvailable) {
    return false;
  }
  const boxItemItself = [...cartItems, ...draftItems].find(item => item.boxItemsId === boxItemsId && item.prod.designUploadMode === DesignUploadMode.box);
  if (!boxItemItself) {
    return false;
  }
  return !(boxItemItself && (isItemInventoryReordered(boxItemItself) || isItemOriginallyInventoryReordered(boxItemItself)));
});
const notecardErrorSelector = createSelector(notecardOptionSelector, checkoutButtonTouchedSelector, (value, checkoutButtonTouched) => {
  if (!checkoutButtonTouched || value !== null) {
    return null;
  }
  return {
    message: 'Please choose if you want to add a notecard to your box.',
    isSuccessed: false,
    colors: {
      message: 'red'
    }
  };
});
const crinklePaperErrorSelector = createSelector(crinklePaperOptionSelector, checkoutButtonTouchedSelector, (value, checkoutButtonTouched) => {
  if (!checkoutButtonTouched || value !== null) {
    return null;
  }
  return {
    message: 'Please let us know what color of crinkle paper you want to be included.',
    isSuccessed: false,
    colors: {
      message: 'red'
    }
  };
});
const isNotecardDesignSavedSelector = createSelector(boxItemsIdSelector, cartItemsSelector, draftItemsSelector, (boxItemsId, cartItems, draftItems) => {
  var _notecard$notecardDes, _notecard$notecardDes2;
  const allItems = [...cartItems, ...draftItems];
  const notecard = allItems.find(item => item.boxItemsId === boxItemsId && item.prod.specialProductType === SPECIAL_PRODUCT_TYPES.BOX_NOTECARD);
  return (notecard === null || notecard === void 0 ? void 0 : (_notecard$notecardDes = notecard.notecardDesignData) === null || _notecard$notecardDes === void 0 ? void 0 : _notecard$notecardDes.textContent) || (notecard === null || notecard === void 0 ? void 0 : (_notecard$notecardDes2 = notecard.notecardDesignData) === null || _notecard$notecardDes2 === void 0 ? void 0 : _notecard$notecardDes2.color);
});
const showNotecardTooltipSelector = createSelector(notecardOptionSelector, isNotecardDesignSavedSelector, (notecardOption, isNotecardDesignSaved) => {
  return notecardOption && !isNotecardDesignSaved;
});
const notecardOptionsSelector = createSelector(notecardOptionSelector, isNotecardDesignSavedSelector, (notecardOption, isNotecardDesignSaved) => {
  const yesLabel = notecardOption && !isNotecardDesignSaved ? 'Yes (Sending own design later)' : 'Yes';
  return [{
    key: 'Yes',
    value: true,
    label: yesLabel
  }, {
    key: 'No',
    value: false,
    label: 'No'
  }];
});
const allowedCrinklePaperVariantsSelector = createSelector(crinklePaperVariantsSelector, crinklePaperOptionSelector, (crinklePaperVariants, crinklePaperOption) => {
  return crinklePaperVariants.filter(({
    name,
    value
  }) => CRINKLE_PAPER_ALLOWED_VARIANTS.has(name) || value === crinklePaperOption);
});
const mapStateToProps = createStructuredSelector({
  notecardOption: notecardOptionSelector,
  crinklePaperOption: crinklePaperOptionSelector,
  crinklePaperVariants: allowedCrinklePaperVariantsSelector,
  isSpecialBoxProductsCustomizable: isSpecialBoxProductsCustomizableSelector,
  notecardError: notecardErrorSelector,
  crinklePaperError: crinklePaperErrorSelector,
  notecardOptions: notecardOptionsSelector,
  showNotecardTooltip: showNotecardTooltipSelector
});
const mapDispatchToProps = dispatch => ({
  onChangeNotecardOption: (boxItemsId, value) => {
    if (value) {
      dispatch(setBoxNotecardBuilderBoxItemsId(boxItemsId));
      return dispatch(showPopup({
        popup: EDIT_BOX_NOTECARD_POPUP
      }));
    }
    dispatch(toggleSpecialProductInBox(boxItemsId, SPECIAL_PRODUCT_TYPES.BOX_NOTECARD, {
      isNotecardPicked: value
    }));
  },
  onChangeCrinklePaperOption: (boxItemsId, value) => {
    dispatch(toggleSpecialProductInBox(boxItemsId, SPECIAL_PRODUCT_TYPES.BOX_CRINKLE_PAPER, {
      crinklePaperVariantIndex: value
    }));
  }
});
export const BoxNotecardAndCrinklePaper = connect(mapStateToProps, mapDispatchToProps)(BoxNotecardAndCrinklePaperComponent);